import Vue from 'vue'
import VueResource from 'vue-resource'
import io from 'socket.io-client'
import IdoniaError from '~/error/IdoniaError'
import store from '~/store/store'
import bus from '~/bus.js'
import { v4 as uuidv4 } from 'uuid'
import { useStream } from '~/commons/stream'
import router from '~/router.js'

const {fetchStream: getStream} = useStream(bus)

Vue.use(VueResource)

export default {
  sockets: new Map(),
  config: {
    accountId: 0,
    idToken: '',
    token: '',
    apiUrl: '',
    wsUrl: '',
    wsID: '',
    authUrl: '',
    ssoUrl: '',
    initializing: true,
    destroyingSession: false
  },
  _addParams (url, extraParams) {
    const params = Object.assign({
      _: Date.now()
    }, extraParams || {})
    const paramsString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&')

    return url.endsWith('?') ? url + paramsString : url + '?' + paramsString
  },
  getAuthHeaders: function (headers = {}) {
    if (this.config.idToken) {
      headers.Authorization = 'Bearer ' + this.config.idToken + ',' + this.config.wsID
    } else {
      const wsID = this.config.wsID || (store.state.notifications.guestSockets[router.currentRoute.params.sha] || {}).wsID || ''
      headers.Authorization = 'IDONIA token=' + this.config.token + ',account_id=' + this.config.accountId + ',socket_id="' + wsID + '"'
    }
    return headers
  },
  // Check if the Authorization header corresponds to a guest session, not relaying on store state nor api.js config
  // this mimics backend IDONIA auth header parsing
  isGuestSession (authHeader) {
    if (!authHeader || !authHeader.includes('IDONIA')) return false

    let token = ''
    let accountId = '0'

    authHeader.split(',').forEach(part => {
      const idx = part.indexOf('=')
      if (idx === -1) return
      const key = part.slice(0, idx).trim()
      const val = part
      .slice(idx + 1)
      .replace(/["']/g, '')
      .trim()

      if (key.endsWith('token')) token = val
      if (key === 'account_id') accountId = val
    })
  // Guest session if a token is present and accountId is 0 or empty
    return token.length > 0 && (accountId === '0' || accountId === '')
  },
  _prepareConfig (skipAuth, headers, params, skipCredentials = false) {
    var config = {}
    config.emulateJSON = true
    headers = {
      'content-type': 'application/json',
      'x-idonia-correlation-id': uuidv4(),
      ...headers
    }
    if (this.isGuestSession(headers.Authorization)) {
      skipCredentials = true
    }
    if (!skipCredentials && store.state.session.csrfToken) {
      headers['X-CSRF-Token'] = store.state.session.csrfToken
    }

    if (!skipAuth && typeof headers.Authorization === 'undefined') {
      headers = this.getAuthHeaders(headers)
    }

    config.headers = headers
    config.params = Object.assign({}, params, { _: Date.now() })
    if (!skipCredentials) config.credentials = true
    return config
  },
  _handleStatus (resp, errorCallback) {
    const body = resp.body || {}
    switch (resp.status) {
      case 401:
        console.error(new IdoniaError('Unauthorized', 3, IdoniaError.API_TYPE, 401))
        // DESTROYING SESSION
        if (this.config.destroyingSession || this.config.initializing) break

        if (!store.getters['session/isAuthenticated'] && !store.getters['session/hasGuestAuthentication']) {
          if (router.currentRoute && typeof router.currentRoute.path === 'string' &&
            router.currentRoute.path.startsWith('/v')) break
          else {
            router.push({ name: 'login' })
            break
          }
        }
        const mlOrigin = store.getters['session/hasGuestAuthentication']
        store.dispatch('session/destroyGuestSession').finally(() => {
          if (store.getters['session/isAuthenticated'] || !mlOrigin) {
            store.dispatch('session/destroySession').then(() => {
              router.push({ name: 'login' })
            })
          } else {
            store.dispatch('session/logout').then(() => {
              router.push(Vue.prototype.$config.defaultMagicLinkURL)
            })
          }
        })
        break
      case 403:
        console.warn(new IdoniaError('Forbidden', 3, IdoniaError.API_TYPE, 403))
        break
      case (resp.status >= 400 && resp.status < 500):
        console.error(new IdoniaError(body.error, 3, IdoniaError.API_TYPE, body.code))
        break
      default:
    }
    console.log('[_handleStatus] ', resp)
    errorCallback(resp)
  },
  post (endpoint, formData, successCallback, errorCallback, skipAuth, headers, onProgress) {
    if (this.config.destroyingSession) return
    successCallback = successCallback || (response => response)
    errorCallback = errorCallback || (response => response)
    skipAuth = skipAuth || false
    headers = headers || {}

    return new Promise((resolve, reject) => {
      const before = typeof onProgress === 'function' ? () => {} : xhr => { resolve(xhr) }
      onProgress = onProgress || (() => { })

      let config = this._prepareConfig(skipAuth, headers)
      config.emulateJSON = false
      config.progress = onProgress
      config.before = before
      Vue.http.post(this.config.apiUrl + endpoint, formData, config)
      .then(resp => {
        successCallback(resp)
        resolve(resp)
      }, (resp) => {
        this._handleStatus(resp, errorCallback)
        reject(resp)
      })
    })
  },
  get (endpoint, params, successCallback, errorCallback, skipAuth, headers) {
    if (this.config.destroyingSession) return
    params = params || {}
    successCallback = successCallback || (response => response)
    errorCallback = errorCallback || (response => response)
    skipAuth = skipAuth || false
    headers = headers || {}

    let config = this._prepareConfig(skipAuth, headers, params)
    return Vue.http.get(this.config.apiUrl + endpoint, config).then(successCallback, response => {
      this._handleStatus(response, errorCallback)
      return response
    })
  },
  download (endpoint, params) {
    this.downloadUrl(this._addParams(this.config.apiUrl + endpoint, params || {}))
  },
  async downloadUrl (url) {
    var frame = document.createElement('iframe')
    frame.src = url
    setTimeout(() => {
      document.body.removeChild(frame)
    }, 10000)
    document.body.appendChild(frame)
  },

  downloadBlob (endpoint, payload = {}) {
    if (this.config.destroyingSession) return Promise.reject(new Error('Session destroying'))
    const { headers = {}, params = {}, fileName } = payload
    const config = this._prepareConfig(true, headers, params)
    config.responseType = 'blob'

    return Vue.http.get(this.config.apiUrl + endpoint, config).then(response => {
      const blob = new Blob([response.body], { type: response.headers.get('content-type') || 'application/octet-stream' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const contentDisposition = response.headers.get('content-disposition')
      let name = fileName || 'download'
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (match && match[1]) {
          name = match[1].replace(/['"]/g, '')
        }
      }
      link.setAttribute('download', name)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      return response
    }).catch(response => {
      this._handleStatus(response, () => {})
      throw response
    })
  },

  downloadBlobCancelable (endpoint, payload = {}) {
    if (this.config.destroyingSession) return { promise: Promise.reject(new Error('Session destroying')), xhr: null }
    const { headers = {}, params = {}, fileName, onProgress } = payload
    const config = this._prepareConfig(false, headers, params)

    const xhr = new XMLHttpRequest()
    const url = this.config.apiUrl + endpoint + (endpoint.includes('?') ? '&' : '?') + '_=' + Date.now()

    const promise = new Promise((resolve, reject) => {
      xhr.open('GET', url, true)
      xhr.responseType = 'blob'

      Object.keys(config.headers).forEach(key => {
        xhr.setRequestHeader(key, config.headers[key])
      })

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const blob = new Blob([xhr.response], { type: xhr.getResponseHeader('content-type') || 'application/octet-stream' })
          const blobUrl = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = blobUrl

          const contentDisposition = xhr.getResponseHeader('content-disposition')
          let name = fileName || 'download'
          if (contentDisposition) {
            const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (match && match[1]) {
              name = match[1].replace(/['"]/g, '')
            }
          }
          link.setAttribute('download', name)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(blobUrl)
          resolve(xhr)
        } else {
          reject(new Error('Download failed: ' + xhr.status))
        }
      }

      xhr.onerror = () => reject(new Error('Download failed'))
      xhr.onabort = () => reject(new Error('Download aborted'))
      if (onProgress) xhr.onprogress = onProgress

      xhr.send()
    })

    return { promise, xhr }
  },
  put (endpoint, params, successCallback, errorCallback, skipAuth, headers) {
    if (this.config.destroyingSession) return
    let config = this._prepareConfig(skipAuth, headers)
    config.emulateJSON = false
    return Vue.http.put(this.config.apiUrl + endpoint, params, config).then(successCallback, (resp) => {
      this._handleStatus(resp, errorCallback)
    })
  },
  delete (endpoint, params, successCallback, errorCallback, skipAuth, headers) {
    if (this.config.destroyingSession) return
    let config = this._prepareConfig(skipAuth, headers)
    return Vue.http.delete(this.config.apiUrl + endpoint, config).then(successCallback, (resp) => {
      this._handleStatus(resp, errorCallback)
    })
  },
  websocket () {
    if (!this.config.idToken && (!this.config.accountId || this.config.accountId === 0 || this.config.accountId === '0')) {
      return null
    }
    if (!this.sockets || !this.sockets.has('account')) {
      let authString = ''
      if (this.config.idToken) {
        authString = 'Bearer ' + this.config.idToken
      } else {
        authString = 'IDONIA token=' + this.config.token + ',account_id=' + this.config.accountId
        if (this.config.scopeHash) {
          authString += ',scope_hash=' + this.config.scopeHash
        }
      }
      let socket = io(this.config.wsUrl, {
        withCredentials: true,
        transports: ['websocket'],
        query: {
          _: Date.now(),
          authorization: authString
        }
      })
      this.sockets.set('account', socket)
    }
    bus.$emit('loadedSocket', this.sockets)
    return this.sockets.get('account')
  },
  guestWebsocket (token) {
    const authString = 'IDONIA token=' + token + ',account_id=0'
    let socket = io(this.config.wsUrl, {
      withCredentials: true,
      transports: ['websocket'],
      query: {
        _: Date.now(),
        authorization: authString
      }
    })
    this.sockets.set('guest-' + token, socket)
    bus.$emit('loadedSocket', this.sockets)
    return socket
  },
  getWebsockets () {
    return this.sockets
  },
  putV2 (endpoint, payload) {
    if (this.config.destroyingSession) return
    let { params, data, skipAuth, headers, progress, before } = payload || {}

    if (
      params === undefined &&
      data === undefined &&
      skipAuth === undefined &&
      headers === undefined &&
      progress === undefined &&
      before === undefined
    ) {
      params = payload
    }

    const config = Object.assign(this._prepareConfig(skipAuth, headers, params), {
      emulateJSON: false,
      progress: progress,
      before: before
    })

    return Vue.http.put(this.config.apiUrl + endpoint, data, config)
      .catch(response => {
        this._handleStatus(response, () => {})
        throw response
      })
  },
  postV2 (endpoint, payload) {
    if (this.config.destroyingSession && endpoint !== 'account/logout') return
    let { params, data, skipAuth, headers, progress, before, skipCredentials } = payload || {}

    if (
      params === undefined &&
      data === undefined &&
      skipAuth === undefined &&
      headers === undefined &&
      progress === undefined &&
      before === undefined &&
      skipCredentials === undefined
    ) {
      params = payload
    }

    const config = Object.assign(this._prepareConfig(skipAuth, headers, params, skipCredentials), {
      emulateJSON: false,
      progress: progress,
      before: before
    })
    return Vue.http.post(this.config.apiUrl + endpoint, data, config)
      .catch(response => {
        this._handleStatus(response, () => {})
        throw response
      })
  },
  remoteGet (url, payload) {
    let { params, skipAuth, headers } = payload || {}

    if (params === undefined && skipAuth === undefined && headers === undefined) {
      params = payload
    }

    const config = this._prepareConfig(skipAuth, headers, params)
    return Vue.http.get(url, config)
      .catch(response => {
        this._handleStatus(response, () => {})
        throw response
      })
  },
  getV2 (endpoint, payload) {
    if (this.config.destroyingSession) return
    let { params, skipAuth, headers, skipCredentials } = payload || {}

    if (params === undefined && skipAuth === undefined &&
      headers === undefined && skipCredentials === undefined) {
      params = payload
    }

    const config = this._prepareConfig(skipAuth, headers, params, skipCredentials)
    return Vue.http.get(this.config.apiUrl + endpoint, config)
      .catch(response => {
        this._handleStatus(response, () => {})
        throw response
      })
  },
  deleteV2 (endpoint, payload) {
    if (this.config.destroyingSession) return
    let { params, skipAuth, headers } = payload || {}

    if (params === undefined && skipAuth === undefined && headers === undefined) {
      params = payload
    }

    const config = this._prepareConfig(skipAuth, headers, params)
    return Vue.http.delete(this.config.apiUrl + endpoint, config)
      .catch(response => {
        this._handleStatus(response, () => {})
        throw response
      })
  },
  fetchStream (endpoint, payload) {
    if (this.config.destroyingSession) return
    let { params, data, skipAuth, headers, before, fetch, callback, eventID } = payload || {}
    if (
      params === undefined &&
      data === undefined &&
      skipAuth === undefined &&
      headers === undefined &&
      before === undefined &&
      fetch === undefined &&
      callback === undefined &&
      eventID === undefined
    ) {
      params = payload
    }

    const config = this._prepareConfig(skipAuth, headers, params)
    const url = new URL(this.config.apiUrl + endpoint)
    Object.keys(config.params).forEach(key => url.searchParams.append(key, config.params[key]))
    const streamConfig = {
      fetch: Object.assign(fetch || {}, {
        body: data,
        headers: config.headers,
        credentials: config.credentials ? 'include' : ''
      }),
      before,
      endpoint,
      eventID
    }
    return getStream(url.toString(), streamConfig, callback)
  }
}



// WEBPACK FOOTER //
// ./src/store/idonia-api/api.js